import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { useCurrency, price } from './shared'
import { t } from '../../../i18n'

// App-style menu: a sticky category rail (left on desktop, a sticky tab
// strip on mobile) that highlights whichever section you're reading, and
// wide horizontal item cards - optimized for jumping around a long menu.
export default function ModernTemplate({ menu, table, addItem, onSelectItem }) {
  const currency = useCurrency()
  const categories = menu.filter(c => c.items?.length)
  const [active, setActive] = useState(categories[0]?.id)

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
      if (visible) setActive(Number(visible.target.dataset.id))
    }, { rootMargin: '-120px 0px -60% 0px' })
    categories.forEach(c => { const el = document.getElementById(`modern-cat-${c.id}`); if (el) observer.observe(el) })
    return () => observer.disconnect()
  }, [menu])

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand,#4f46e5)]">{t("Order from your phone")}</p>
          <h1 className="text-4xl font-bold text-slate-900 mt-1">{t("Menu")}</h1>
        </div>
        {table && <span className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold">{t("Table")} {table}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-8 items-start">
        <nav className="min-w-0 sticky top-16 lg:top-24 z-20 -mx-4 px-4 lg:mx-0 lg:px-0 bg-[var(--theme-background,#faf9f6)] py-2 lg:py-0 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar" aria-label={t("Menu sections")}>
          {categories.map(c => (
            <a key={c.id} href={`#modern-cat-${c.id}`}
              className={`whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active === c.id ? 'bg-[color:var(--brand,#4f46e5)] text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}>
              {c.name}<span className="ms-2 text-xs opacity-60">{c.items.length}</span>
            </a>
          ))}
        </nav>

        <div className="min-w-0 space-y-12">
          {categories.map(c => (
            <section key={c.id} id={`modern-cat-${c.id}`} data-id={c.id} className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{c.name}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {c.items.map(item => (
                  <article key={item.id} onClick={() => onSelectItem(item)} className="cursor-pointer flex gap-4 bg-white rounded-2xl p-3 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-28 h-28 shrink-0 rounded-xl overflow-hidden bg-slate-100">
                      {item.image && <img loading="lazy" src={item.image} alt={item.name} className="w-full h-full object-cover" onError={e => { e.currentTarget.style.display = 'none' }} />}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col py-1">
                      <h3 className="font-semibold text-slate-900 leading-snug">{item.name}</h3>
                      {item.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t(item.description)}</p>}
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <span className="font-bold text-slate-900">{price(item.price, currency)}</span>
                        <button onClick={e => { e.stopPropagation(); addItem(item) }} aria-label={t("Add {name}", { name: item.name })} className="w-9 h-9 rounded-full bg-[color:var(--brand,#4f46e5)] text-white grid place-items-center hover:bg-[color:var(--brand,#4338ca)]"><Plus size={18} /></button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}
